/**
 * Renderer backend factory.
 *
 * Default backend is WebGLRenderer (Y2K Console).  Canvas2D (80s Arcade) is
 * available via `?renderer=canvas2d` URL param or by setting
 * `localStorage.rendererBackend = 'canvas2d'` (the settings panel writes this
 * when the user explicitly chooses 80s Arcade).
 *
 * Both renderers satisfy the same interface contract:
 *   - constructor: (canvases, game, ui, eventFeed, performanceOverlay)
 *   - methods:     setScaleFactor, renderBackground, renderGameLayer, renderUiLayer,
 *                  getWorldTransform, setEntitySystemOverride
 *   - live refs:   gridSystem, entitySystem, effectSystem are read-only getters
 *                  that track game.locationGrid/entitySystem/effectSystem, so
 *                  Game.loadLevel needs no re-wiring
 *   - exposed:     canvas (fg), context (UI canvas's 2D context for editor compat)
 */

import { Canvas2DRenderer } from '../renderer.js';
import { gameEventLog } from '../logging.js';
import { getStorage } from '../storage.js';
import { WebGLRenderer } from '../webgl_renderer.js';

/**
 * @returns {boolean}
 */
function isWebGLRequested() {
    if (typeof window === 'undefined') return false;
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('renderer') === 'webgl')    return true;
        if (params.get('renderer') === 'canvas2d') return false;
    } catch { /* fallthrough to stored preference */ }
    try {
        // WebGL is the default; opt-out by setting rendererBackend = 'canvas2d'.
        const stored = getStorage().getItem('rendererBackend');
        return stored !== 'canvas2d';
    } catch {
        return true;   // default to WebGL if storage is unavailable
    }
}

/**
 * Create the renderer chosen by the current backend flag or an explicit type.
 *
 * @param {{bg: HTMLCanvasElement, fg: HTMLCanvasElement, ui: HTMLCanvasElement}} canvases
 * @param {import('../game.js').Game} game
 * @param {import('../ui.js').UI} ui
 * @param {import('../util.js').EventFeed} eventFeed
 * @param {import('../util.js').PerformanceOverlay} performanceOverlay
 * @param {string|null} [explicitType]  'webgl' | 'canvas2d' | null (auto-detect)
 * @returns {Canvas2DRenderer | WebGLRenderer}
 */
export function createRenderer(canvases, game, ui, eventFeed, performanceOverlay, explicitType = null) {
    const useWebGL = explicitType ? explicitType === 'webgl' : isWebGLRequested();
    const ctorArgs = [canvases, game, ui, eventFeed, performanceOverlay];
    if (useWebGL) {
        gameEventLog('[renderer] Using WebGLRenderer');
        return new WebGLRenderer(...ctorArgs);
    }
    return new Canvas2DRenderer(...ctorArgs);
}

/** @returns {'webgl'|'canvas2d'} */
export function getActiveRendererType() {
    return isWebGLRequested() ? 'webgl' : 'canvas2d';
}
